import type { NoteResponse } from '@emr/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessage } from '../../../api/client.ts';
import { cosignNote, signNote } from '../../../api/notes.ts';
import { useGrants, useSession } from '../../../auth/sessionContext.ts';
import { Badge, Button, ErrorText } from '../../../components/primitives.tsx';
import { summarizeText } from '../../../components/summarizeText.ts';
import { formatDateTime } from '../../../lib/dateFormat.ts';
import { SignatureMark } from '../../../signature/SignatureMark.tsx';
import { cosignAvailability, signAvailability, type NoteActionState } from '../noteActions.ts';

/** Cards, not table rows: `content` runs up to `MAX_NOTE_CONTENT_LENGTH` (20,000 characters,
 * `shared/src/encounterRequests.ts`) — the "long, free-text field" half of
 * `claude-kit/rules/invariants.md`'s table-column guidance, not the "short field" half `DataTable`
 * covers. Summarized by default, with an explicit control to read the rest. */
const CONTENT_SUMMARY_CHARS = 320;

export function NoteCard({
  note,
  patientId,
}: {
  readonly note: NoteResponse;
  readonly patientId: string;
}) {
  const { t } = useTranslation('patientChartTabs');
  const session = useSession();
  const grants = useGrants();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { summary, truncated } = summarizeText(note.content, CONTENT_SUMMARY_CHARS);

  const invalidateNotes = () =>
    queryClient.invalidateQueries({ queryKey: ['patient', patientId, 'notes'] });

  const sign = useMutation({
    mutationFn: () => signNote(note.id),
    onSuccess: () => {
      void invalidateNotes();
    },
  });
  const cosign = useMutation({
    mutationFn: () => cosignNote(note.id),
    onSuccess: () => {
      void invalidateNotes();
    },
  });

  const signState = signAvailability(note, grants, session.userId);
  const cosignState = cosignAvailability(note, grants, session.userId);

  return (
    <li className="flex flex-col gap-2 rounded-control border border-line p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink">{note.noteType}</span>
        <Badge tone={note.status === 'signed' ? 'success' : 'neutral'}>{note.status}</Badge>
      </div>

      <p className="m-0 whitespace-pre-wrap break-words text-ink [overflow-wrap:anywhere]">
        {expanded ? note.content : summary}
        {truncated && !expanded ? '…' : ''}
      </p>
      {truncated ? (
        <button
          type="button"
          onClick={() => {
            setExpanded((value) => !value);
          }}
          className="self-start text-sm font-medium text-accent-text hover:underline"
        >
          {expanded ? t('noteCard.showLess') : t('noteCard.showMore')}
        </button>
      ) : null}

      <div className="flex flex-col gap-2 text-xs text-ink-muted">
        <span>{t('noteCard.createdAt', { date: formatDateTime(note.createdAt) })}</span>
        {note.signedAt !== null ? (
          <SignatureAttestation
            variant="signed"
            at={note.signedAt}
            signature={note.signedSignature}
          />
        ) : null}
        {note.cosignedAt !== null ? (
          <SignatureAttestation
            variant="cosigned"
            at={note.cosignedAt}
            signature={note.cosignedSignature}
          />
        ) : null}
      </div>

      <div className="flex flex-wrap gap-3">
        <NoteActionButton
          label={t('noteCard.signAction')}
          state={signState}
          pending={sign.isPending}
          onClick={() => {
            sign.mutate();
          }}
        />
        <NoteActionButton
          label={t('noteCard.cosignAction')}
          state={cosignState}
          pending={cosign.isPending}
          onClick={() => {
            cosign.mutate();
          }}
        />
      </div>
      {sign.isError ? <ErrorText message={errorMessage(sign.error)} /> : null}
      {cosign.isError ? <ErrorText message={errorMessage(cosign.error)} /> : null}
    </li>
  );
}

/**
 * One attestation event's date, mark, and signature line — the snapshot captured at the moment of
 * signing (`@emr/shared`'s `NoteResponse.signedSignature`/`cosignedSignature`, `server/src/db/schema/
 * clinical.ts`'s own comment on why it is a snapshot and never a live reference). `signature === null`
 * covers both "signed before this feature existed" and "signed by someone who had never set a mark
 * at the time" — `SignatureMark` already renders a plain "No mark on file" placeholder for either, so
 * nothing extra is needed here beyond passing it through.
 */
function SignatureAttestation({
  variant,
  at,
  signature,
}: {
  readonly variant: 'signed' | 'cosigned';
  readonly at: string;
  readonly signature: NoteResponse['signedSignature'];
}) {
  const { t } = useTranslation('patientChartTabs');
  const key = variant === 'signed' ? 'noteCard.signedAt' : 'noteCard.cosignedAt';
  return (
    <div className="flex flex-col gap-1 border-t border-line pt-2 first:border-t-0 first:pt-0">
      <span>{t(key, { date: formatDateTime(at) })}</span>
      <SignatureMark
        settings={signature}
        className="h-12 w-full max-w-[16rem] text-lg text-ink [overflow-wrap:anywhere]"
      />
      {signature?.signatureLine !== null && signature?.signatureLine !== undefined ? (
        <span className="text-ink">{signature.signatureLine}</span>
      ) : null}
    </div>
  );
}

/**
 * One action's button, given its already-computed `NoteActionState` (`noteActions.ts`).
 *
 * `hidden` renders nothing — the task brief's own instruction to gate on capability, not just
 * hide-on-403. `disabled` renders the button greyed out with its reason visible underneath, so a
 * clinician who does hold `note:sign`/`note:cosign` in general can see *why* this particular note
 * refuses it, rather than wondering whether the feature exists at all.
 */
function NoteActionButton({
  label,
  state,
  pending,
  onClick,
}: {
  readonly label: string;
  readonly state: NoteActionState;
  readonly pending: boolean;
  readonly onClick: () => void;
}) {
  if (state.kind === 'hidden') return null;
  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="secondary"
        pending={pending}
        disabled={state.kind === 'disabled'}
        onClick={onClick}
      >
        {label}
      </Button>
      {state.kind === 'disabled' ? (
        <span className="max-w-[24ch] text-xs text-ink-muted">{state.reason}</span>
      ) : null}
    </div>
  );
}
